import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";

import { formatMoneyFromCents } from "../../../helpers/money";
import { DayCloseTrendPoint } from "../../../services/dayCloseReportService";

type Props = {
  trend: DayCloseTrendPoint[];
  currencyLocale: string;
};

const DayCloseTrendChart = ({ trend, currencyLocale }: Props) => {
  const { t } = useTranslation();

  const categories = useMemo(
    () =>
      trend.map((point) => {
        const [, month, day] = point.date.split("-");
        return `${day}/${month}`;
      }),
    [trend]
  );

  const series = useMemo(
    () => [
      {
        name: t("studio.financial.dayClose.trend.series"),
        data: trend.map((point) => Number((point.net_cents / 100).toFixed(2))),
      },
    ],
    [trend, t]
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "area" as const,
        toolbar: { show: false },
        zoom: { enabled: false },
        sparkline: { enabled: false },
      },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth" as const, width: 2 },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0.4,
          opacityFrom: 0.35,
          opacityTo: 0.05,
        },
      },
      colors: ["#0F4C3A"],
      xaxis: {
        categories,
        labels: { style: { fontSize: "11px" } },
      },
      yaxis: {
        labels: {
          formatter: (value: number) =>
            formatMoneyFromCents(Math.round(value * 100), currencyLocale),
          style: { fontSize: "11px" },
        },
      },
      tooltip: {
        y: {
          formatter: (value: number) =>
            formatMoneyFromCents(Math.round(value * 100), currencyLocale),
        },
      },
      grid: {
        borderColor: "#f0f0f0",
        strokeDashArray: 3,
      },
    }),
    [categories, currencyLocale]
  );

  if (trend.length === 0) {
    return null;
  }

  return (
    <ReactApexChart
      dir="ltr"
      options={options}
      series={series}
      type="area"
      height={220}
      className="apex-charts"
    />
  );
};

export default DayCloseTrendChart;
